//home.js
import React, { useState, useEffect, useRef } from "react";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import useChatSession from "../hooks/useChatSession";
import useAuthRedirect from "../hooks/useAuthRedirect";
import useSendMessage from "../hooks/useSendMessage";

import MainChatLayout from "../components/MainChatLayout";
import SidebarContainer from "../components/SidebarContainer";

export default function Home({ user }) {

  const supabase = useSupabaseClient();
  useAuthRedirect(user);

  const [currentChatId, setCurrentChatId] = useState(null);
  const [input, setInput] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const {
    chatList,
    conversation,
    setConversation,
    fetchChatSessions,
    fetchChatHistory,
  } = useChatSession(supabase, user);

  // 1️⃣ Load sessions on login
  useEffect(() => {
    if (user) fetchChatSessions();
  }, [user, fetchChatSessions]);

  // 2️⃣ Auto-select the first chat if none chosen yet
  useEffect(() => {
    if (chatList.length > 0 && !currentChatId) {
      const firstId = chatList[0].id;
      setCurrentChatId(firstId);
      fetchChatHistory(firstId);
    }
  }, [chatList, currentChatId, fetchChatHistory]);

  // 3️⃣ Scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation]);

  const handleSend = useSendMessage({
    conversation,
    setConversation,
    currentChatId,
    setLoading,
    setInput,
    fetchChatHistory,
    fetchChatSessions,
    supabase,
    setCurrentChatId,
  });
  const handleNewChat = async () => {
  const { data, error } = await supabase
    .from("chat_sessions")
    .insert([{ user_id: user.id, title: "New chat" }])
    .select()
    .single();

  if (error) {
    console.error("❌ Failed to create new chat:", error);
    return;
  }

  // Immediately select and switch to the new chat
  setCurrentChatId(data.id);
setConversation({ id: data.id, messages: [] });
  fetchChatSessions();
};

  return (
    <div className="flex h-screen overflow-hidden bg-gradient-to-tr from-[#1e3a8a] via-[#7c3aed] to-[#0ea5e9] text-white">
      <SidebarContainer className="w-72 flex-shrink-0"
        user={user}
        supabase={supabase}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        chatList={chatList}
        onNewChat={handleNewChat}
        currentChatId={currentChatId}
        setCurrentChatId={setCurrentChatId}
        setConversation={setConversation}
        fetchChatSessions={fetchChatSessions}
        fetchChatHistory={fetchChatHistory}
      />
      <MainChatLayout className="flex-1"
        user={user}
        supabase={supabase}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        conversation={conversation}
        currentChatId={currentChatId}
        setConversation={setConversation} 
        fetchChatSessions={fetchChatSessions}
        fetchChatHistory={fetchChatHistory}
        loading={loading}
        setLoading={setLoading}
        messagesEndRef={messagesEndRef}
        input={input}
        setInput={setInput}
      />
    </div>
  );
}